import React, { useMemo, useState } from "react";
import TableClient from "@/components/tables/TableClient";
import StatusChip from "@/components/muiCustom/chip/StatusChip";
import { Visibility } from "@mui/icons-material";
import { convertValue } from "@/features/campaigns/email/utils/emailCampaignHelper";
import EmailCampaignDetailsModal from "./EmailCampaignDetailsModal";
import EmailListModal from "./EmailListModal";

type ReportMetricValue = number | string | null | undefined;

type EmailCampaignReportRow = {
  id?: string | number;
  locationName?: string;
  sendDate?: string;
  status?: string;
  customerList?: string;
  template?: string;
  emailsSent?: number;
  delivered?: ReportMetricValue;
  uniqueOpens?: ReportMetricValue;
  conversions?: ReportMetricValue;
  bounces?: ReportMetricValue;
  image?: string;
  [key: string]: unknown;
};

type EmailCampaignReportTableProps = {
  campaignsData?: EmailCampaignReportRow[] | null;
  selectedTab: number;
};

type TableCellArgs = {
  value?: ReportMetricValue;
  row: {
    original: EmailCampaignReportRow;
  };
};

type ModalState =
  | false
  | {
      isOpen: boolean;
      image?: string;
    };

const EmailCampaignReportTable = ({
  campaignsData,
  selectedTab,
}: EmailCampaignReportTableProps) => {
  const [modalOpen, setModalOpen] = useState<ModalState>(false);
  const [emailListModalOpen, setEmailListModalOpen] = useState(false);
  const [selectedCustomerList, setSelectedCustomerList] = useState("");

  const handleViewDetails = (value: TableCellArgs["row"]) => {
    setModalOpen({ isOpen: true, image: value?.original?.image });
  };

  const handleCloseModal = () => {
    setModalOpen({ isOpen: false, image: "" });
  };

  const handleCustomerListClick = (customerListName?: ReportMetricValue) => {
    setSelectedCustomerList(String(customerListName ?? ""));
    setEmailListModalOpen(true);
  };

  const handleCloseEmailListModal = () => {
    setEmailListModalOpen(false);
    setSelectedCustomerList("");
  };
  const columns = useMemo(
    () => [
      {
        accessor: "locationName",
        width: 100,
        Cell: ({ value }: TableCellArgs) => (
          <div
            className="d-flex align-items-center"
            style={{ height: "100%", width: "100px" }}
          >
            <span
              className="fw-sb text-start table-cell"
              style={{
                width: "100%",
              }}
              title={value as string}
            >
              {value}
            </span>
          </div>
        ),
        Header: () => (
          <div
            className="text-start fw-bold table-cell"
            style={{
              width: "100px",
            }}
          >
            Location Name
          </div>
        ),
      },
      {
        accessor: "sendDate",
        width: 100,
        Cell: ({ value }: TableCellArgs) => (
          <div
            className="d-flex align-items-center"
            style={{ height: "100%", width: "100px" }}
          >
            <span
              className=" text-start table-cell fw-sb"
              style={{
                width: "100%",
              }}
              title={value as string}
            >
              {value}
            </span>
          </div>
        ),
        Header: () => (
          <div
            className="text-start fw-bold table-cell"
            style={{
              width: "100px",
            }}
          >
            Send Date
          </div>
        ),
      },
      {
        accessor: "status",
        width: 80,
        Cell: ({ value }: TableCellArgs) => (
          <div
            className="d-flex align-items-center"
            style={{ height: "100%", width: "80px" }}
          >
            <div
              className="text-start table-cell fw-sb"
              style={{
                width: "100%",
              }}
            >
              <StatusChip
                label={value}
                status={value === "Sent" ? "success" : "warning"}
                showOnlyLabel
              />
            </div>
          </div>
        ),
        Header: () => (
          <div
            className="text-start fw-bold table-cell"
            style={{
              width: "80px",
            }}
          >
            Status
          </div>
        ),
      },
      {
        accessor: "customerList",
        width: 200,
        Cell: ({ value }: TableCellArgs) => (
          <div
            className="d-flex align-items-center"
            style={{ height: "100%", width: "200px" }}
          >
            <div className="text-start" style={{ width: "100%" }}>
              <div
                className="p-2 fw-b table-cell"
                style={{
                  color: "var(--primary-base)",
                  backgroundColor: "var(--primary-tint)",
                  borderRadius: "var(--radius-control)",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  maxWidth: "100%",
                  width: "100%",
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLDivElement).style.backgroundColor =
                    "var(--primary-tint-hover)";
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLDivElement).style.backgroundColor =
                    "var(--primary-tint)";
                }}
                onClick={() => handleCustomerListClick(value)}
                title={value as string}
              >
                {value}
              </div>
            </div>
          </div>
        ),
        Header: () => (
          <div
            className="text-start fw-bold table-cell"
            style={{
              width: "200px",
            }}
          >
            Customer List
          </div>
        ),
      },
      {
        accessor: "template",
        width: 120,
        Cell: ({ value }: TableCellArgs) => (
          <div
            className="d-flex align-items-center"
            style={{ height: "100%", width: "120px" }}
          >
            <div
              className="fw-sb text-start table-cell"
              style={{
                width: "100%",
              }}
              title={value as string}
            >
              {value}
            </div>
          </div>
        ),
        Header: () => (
          <div
            className="text-start fw-bold table-cell"
            style={{
              width: "120px",
            }}
          >
            Template
          </div>
        ),
      },
      {
        accessor: "emailsSent",
        width: 100,
        Cell: ({ value }: TableCellArgs) => (
          <div
            className="d-flex align-items-center"
            style={{ height: "100%", width: "100px" }}
          >
            <div
              className="fw-sb text-start table-cell"
              style={{
                width: "100%",
              }}
            >
              {convertValue(value, "number")}
            </div>
          </div>
        ),
        Header: () => (
          <div
            className="text-start fw-bold table-cell"
            style={{
              width: "100px",
            }}
          >
            Emails Sent
          </div>
        ),
      },
      {
        accessor: "delivered",
        width: 80,
        Cell: ({ value, row }: TableCellArgs) => {
          const isPercentageView = selectedTab === 1;
          const targetType = isPercentageView ? "percentage" : "number";
          const total = row.original.emailsSent;
          return (
            <div
              className="d-flex align-items-center"
              style={{ height: "100%", width: "80px" }}
            >
              <div
                className="fw-sb text-start table-cell"
                style={{
                  width: "100%",
                }}
              >
                {convertValue(value, targetType, total)}
              </div>
            </div>
          );
        },
        Header: () => (
          <div
            className="text-start fw-bold table-cell"
            style={{
              width: "80px",
            }}
          >
            Delivered
          </div>
        ),
      },
      {
        accessor: "uniqueOpens",
        width: 100,
        Cell: ({ value, row }: TableCellArgs) => {
          const isPercentageView = selectedTab === 1;
          const targetType = isPercentageView ? "percentage" : "number";
          const total = row.original.emailsSent;
          return (
            <div
              className="d-flex align-items-center"
              style={{ height: "100%", width: "100px" }}
            >
              <div
                className="fw-sb text-start table-cell"
                style={{
                  width: "100%",
                }}
              >
                {convertValue(value, targetType, total)}
              </div>
            </div>
          );
        },
        Header: () => (
          <div
            className="text-start fw-bold table-cell"
            style={{
              width: "100px",
            }}
          >
            Unique Opens
          </div>
        ),
      },
      {
        accessor: "conversions",
        width: 100,
        Cell: ({ value, row }: TableCellArgs) => {
          const isPercentageView = selectedTab === 1;
          const targetType = isPercentageView ? "percentage" : "number";
          const total = row.original.emailsSent;
          return (
            <div
              className="d-flex align-items-center"
              style={{ height: "100%", width: "100px" }}
            >
              <div
                className="fw-sb text-start table-cell"
                style={{
                  width: "100%",
                }}
              >
                {convertValue(value, targetType, total)}
              </div>
            </div>
          );
        },
        Header: () => (
          <div
            className="text-start fw-bold table-cell"
            style={{
              width: "100px",
            }}
          >
            Conversions
          </div>
        ),
      },
      {
        accessor: "bounces",
        width: 80,
        Cell: ({ value, row }: TableCellArgs) => {
          const isPercentageView = selectedTab === 1;
          const targetType = isPercentageView ? "percentage" : "number";
          const total = row.original.emailsSent;
          return (
            <div
              className="d-flex align-items-center"
              style={{ height: "100%", width: "80px" }}
            >
              <div
                className="fw-sb text-start table-cell"
                style={{
                  width: "100%",
                }}
              >
                {convertValue(value, targetType, total)}
              </div>
            </div>
          );
        },
        Header: () => (
          <div
            className="text-start fw-bold table-cell"
            style={{
              width: "80px",
            }}
          >
            Bounces
          </div>
        ),
      },
      {
        accessor: "actions",
        width: 60,
        Cell: ({ row }: TableCellArgs) => (
          <div
            className="d-flex align-items-center"
            style={{ height: "100%", width: "60px" }}
          >
            <div
              className="text-start table-cell"
              style={{
                width: "100%",
              }}
            >
              <Visibility
                style={{ color: "var(--primary-base)", cursor: "pointer" }}
                fontSize="small"
                onClick={() => handleViewDetails(row)}
              />
            </div>
          </div>
        ),
        Header: () => (
          <div
            className="text-start fw-bold"
            style={{
              width: "60px",
            }}
          >
            Actions
          </div>
        ),
      },
    ],
    [selectedTab]
  );

  return (
    <div className="mt-4">
      <div className="card">
        <div className="card-body p-0 email-campaign-report-table">
          <TableClient
            data={campaignsData || []}
            columns={columns}
            showSearch={false}
            pagination={true}
            initialPageSize={10}
            totalRecords={campaignsData?.length || 0}
            filters={null}
          />
        </div>
      </div>

      {/* Email Campaign Details Modal */}
      <EmailCampaignDetailsModal
        open={typeof modalOpen === "object" ? modalOpen.isOpen : undefined}
        onClose={handleCloseModal}
        imageSrc={typeof modalOpen === "object" ? modalOpen.image : undefined}
      />

      {/* Email List Modal */}
      <EmailListModal
        open={emailListModalOpen}
        onClose={handleCloseEmailListModal}
        customerListName={selectedCustomerList}
      />
    </div>
  );
};

export default EmailCampaignReportTable;
